import { Prescription } from '../../../../types/models/Presc'

/**
 * 一覧の 1 行 = 1 OCRRequestEvent。行の identity は ocr_request_event_id(ADR-0029)。
 *
 * backend の一覧APIは get_latest_version_prescriptions_of_ocr_requests でイベントごとの
 * 最新 edit_version を選定済みだが、2回目以降のポーリングは /additional_presc/ の差分を
 * **クライアント側のキャッシュにマージ**する(usePrescriptionsQuery)。値を修正すると
 * 同じイベントに別 id の Prescription が増えるため、helpers/getUniquePrescriptions
 * (Prescription.id で重複排除)を通しても同じイベントが2行残る。ここで畳む。
 *
 * **過分割(1アップロードが複数イベントに割れる)は畳まない。** 別イベントは別の行として出す
 * (束ねると割れている事実だけが見えなくなる。直すのは backend)。
 */
export const foldByOcrRequestEvent = (prescriptions: Prescription[]) => {
  const latestOfEvent = new Map<string, Prescription>()
  prescriptions.forEach((presc) => {
    const held = latestOfEvent.get(presc.ocr_request_event_id)
    if (!held || presc.edit_version > held.edit_version) {
      latestOfEvent.set(presc.ocr_request_event_id, presc)
    }
  })
  return [...latestOfEvent.values()].sort(
    (a, b) =>
      new Date(b.request_event_created_at).getTime() -
      new Date(a.request_event_created_at).getTime()
  )
}

/**
 * 行見出しの患者名。JAHIS QR直読は OCR を通らないため backend が basic_result=null を返す。
 * その場合は serializer が組み立てた label(`[QR] 患者名`)にフォールバックする。
 * 一次情報: prescription/serializers.py PrescriptionSerializer.get_label
 */
export const getPatientName = (presc: Prescription) =>
  presc.basic_result?.id_11_patient_name_kanji?.selected_option || presc.label

export const getHospitalName = (presc: Prescription) =>
  presc.basic_result?.id_1_hospital_name?.selected_option ?? ''

/**
 * 突合の対象になっていない行。QR直読は OCR 結果がそもそも生まれない
 * (record_saver.py が QR 検出時は OCR を通さず、中身はレセコンへ直接送る)。
 * 杏林堂の実測で 57%。**隠さず沈める**(ADR-0029)。
 */
export const isQrOnly = (presc: Prescription) =>
  presc.was_read_using_jahis_qr && !presc.basic_result

export const matchesSearch = (presc: Prescription, search: string) => {
  const needle = search.trim().toLowerCase()
  if (!needle) return true
  return getPatientName(presc).toLowerCase().includes(needle)
}

export type DateGroup = {
  key: string
  label: string
  rows: Prescription[]
}

const WEEKDAY_JP = ['日', '月', '火', '水', '木', '金', '土']

/**
 * 日付の見出しで区切る。legacy の日付ナビ(◀ ▶ + 当日フィルタ)の置き換えで、
 * **どの日も一覧から消えない**(フィルタ軸を増やさない)。入力は新しい順である前提。
 */
export const groupByDate = (prescriptions: Prescription[]): DateGroup[] => {
  const groups: DateGroup[] = []
  prescriptions.forEach((presc) => {
    const at = new Date(presc.request_event_created_at)
    const key = `${at.getFullYear()}-${at.getMonth() + 1}-${at.getDate()}`
    const last = groups[groups.length - 1]
    if (last?.key === key) {
      last.rows.push(presc)
      return
    }
    groups.push({
      key,
      label: `${at.getMonth() + 1}月${at.getDate()}日 (${
        WEEKDAY_JP[at.getDay()]
      })`,
      rows: [presc],
    })
  })
  return groups
}
